import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { RootState } from "app.store";
import momentTz from "moment-timezone";
import { shallowEqual, useSelector } from "react-redux";
import { CustomNumberInput } from "shared/components/custom-input/custom-number-input";
import { MoneyInput } from "shared/components/custom-input/money-input";
import { ProductAttributeSelector } from "shared/components/product-attribute/product-attr-selector";
import { ProductAttributeValueSelector } from "shared/components/product-attribute/product-attr-value-selector";
import { TagSelector } from "shared/components/tag/tag-selector";
import { UnitSelector } from "shared/components/unit/unit-selector";
import { UploadFileLocal } from "shared/components/upload-file/upload-file";
import { SeoModel } from "shared/models/seo-model";
import {
  CoreResource,
  momentDateTimeFormat,
  t,
} from "shared/plugins/localization";
import { cleanUnicode } from "shared/plugins/string-helper";
import {
  ProductAttributeModel,
  ProductAttributeValueModel,
} from "states/product-attribute/product-attribute.models";
import { ProductCategoryModel } from "states/product-category/product-category.models";
import {
  PriceBySubTotalItemModel,
  ProductModel,
  ProductVariationAttributeModel,
  WholesaleItemModel,
  WholesalePriceType,
} from "states/product/product.models";
import { TagModel } from "states/tag/tag.models";
import { UnitModel } from "states/unit/unit.models";

export interface IProductInformationTabProps {
  model: ProductModel;
  onChange: (newModel: ProductModel) => void;
  productCategories: ProductCategoryModel[];
  isUpdateMode: boolean;
  isUnitProduct: boolean;
}

export function ProductInformationTab(props: IProductInformationTabProps) {
  const { model, onChange, productCategories, isUpdateMode, isUnitProduct } =
    props;

  const currentTimeZone = useSelector(
    (state: RootState) => state.globalValue.ianaTimeZone,
    shallowEqual
  );

  const _handleAddVariationAttribute = () => {
    if (!model.variationAttributes) {
      model.variationAttributes = [];
    }
    model.variationAttributes.push({} as ProductVariationAttributeModel);
    onChange({ ...model, variationAttributes: [...model.variationAttributes] });
  };

  const _handleAddWholeSale = () => {
    if (!model.wholesale) {
      model.wholesale = { items: [] };
    }
    model.wholesale.items.push({} as WholesaleItemModel);
    onChange({
      ...model,
      wholesale: { ...model.wholesale, items: [...model.wholesale.items] },
    });
  };

  const _handleAddPriceBySubTotal = () => {
    if (!model.priceBySubTotal) {
      model.priceBySubTotal = { items: [] };
    }
    model.priceBySubTotal.items.push({} as PriceBySubTotalItemModel);
    onChange({
      ...model,
      priceBySubTotal: {
        ...model.priceBySubTotal,
        items: [...model.priceBySubTotal.items],
      },
    });
  };

  const wholesaleColumns: ColumnsType<WholesaleItemModel> = [
    {
      title: "Số lượng từ",
      dataIndex: "quantityFrom",
      key: "quantityFrom",
      render: (quantityFrom, item) => (
        <CustomNumberInput
          value={item.quantityFrom}
          onChange={(value) => {
            item.quantityFrom = value;
            onChange({ ...model });
          }}
          placeholder="Số lượng từ"
        />
      ),
    },
    {
      title: "Số lượng đến",
      dataIndex: "quantityTo",
      key: "quantityTo",
      render: (quantityTo, item) => (
        <CustomNumberInput
          value={item.quantityTo}
          onChange={(value) => {
            item.quantityTo = value;
            onChange({ ...model });
          }}
          placeholder="Số lượng đến"
        />
      ),
    },
    {
      title: "Giá tiền",
      dataIndex: "price",
      key: "price",
      render: (price, item) => (
        <MoneyInput
          value={item.sellingPrice}
          onChange={(value) => {
            item.sellingPrice = value;
            onChange({ ...model });
          }}
          placeholder="Giá tiền"
        />
      ),
    },
    {
      title: "Loại",
      dataIndex: "price",
      key: "price",
      render: (price, item) => (
        <Select
          value={item.priceType}
          placeholder="Chọn loại giá"
          onChange={(value) => {
            item.priceType = value;
            onChange({ ...model });
          }}
        >
          <Select.Option value={WholesalePriceType.Unit}>Đơn giá</Select.Option>
          <Select.Option value={WholesalePriceType.Total}>
            Giá cho tất cả
          </Select.Option>
        </Select>
      ),
    },
    {
      title: "",
      width: "100px",
      render: (attribute, item, index) => (
        <Popconfirm
          title={t(CoreResource, "QuestionRemove")}
          onConfirm={() => {
            model.wholesale?.items.splice(index, 1);
            onChange({
              ...model,
              wholesale: {
                ...model.wholesale,
                items: [...(model.wholesale?.items ?? [])],
              },
            });
          }}
          okText="Có"
          cancelText="Không"
        >
          <Button danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const priceBySubTotalColumns: ColumnsType<PriceBySubTotalItemModel> = [
    {
      title: "Giá trị đơn hàng từ",
      dataIndex: "subTotalFrom",
      key: "subTotalFrom",
      render: (subTotalFrom, item) => (
        <MoneyInput
          value={item.subTotalFrom}
          onChange={(value) => {
            item.subTotalFrom = value;
            onChange({ ...model });
          }}
          placeholder="Giá trị đơn hàng từ"
        />
      ),
    },
    {
      title: "Giá trị đơn hàng đến",
      dataIndex: "subTotalTo",
      key: "subTotalTo",
      render: (subTotalTo, item) => (
        <MoneyInput
          value={item.subTotalTo}
          onChange={(value) => {
            item.subTotalTo = value;
            onChange({ ...model });
          }}
          placeholder="Số lượng đến"
        />
      ),
    },
    {
      title: "Giá tiền",
      dataIndex: "price",
      key: "price",
      render: (price, item) => (
        <MoneyInput
          value={item.sellingPrice}
          onChange={(value) => {
            item.sellingPrice = value;
            onChange({ ...model });
          }}
          placeholder="Giá tiền"
        />
      ),
    },
    {
      title: "",
      width: "100px",
      render: (attribute, item, index) => (
        <Popconfirm
          title={t(CoreResource, "QuestionRemove")}
          onConfirm={() => {
            model.priceBySubTotal?.items.splice(index, 1);
            onChange({
              ...model,
              priceBySubTotal: {
                ...model.priceBySubTotal,
                items: [...(model.priceBySubTotal?.items ?? [])],
              },
            });
          }}
          okText="Có"
          cancelText="Không"
        >
          <Button danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const variationColumns: ColumnsType<ProductVariationAttributeModel> = [
    {
      title: "Thuộc tính",
      dataIndex: "attribute",
      key: "attribute",
      width: "50%",
      render: (attribute, item) => (
        <ProductAttributeSelector
          onChange={(value) => {
            let valueTemp = value as ProductAttributeModel;
            if (!valueTemp) return;
            item.variationAttributeId = valueTemp.id;
            onChange({ ...model });
          }}
          isMultiple={false}
          value={item.variationAttributeId}
          disabled={Boolean(
            isUpdateMode || model.mainVariationProductId || isUnitProduct
          )}
        />
      ),
    },
    {
      title: "Giá trị",
      dataIndex: "attributeValue",
      key: "attributeValue",
      width: "50%",
      render: (attribute, item) => (
        <ProductAttributeValueSelector
          onChange={(value) => {
            let valueTemp = value as ProductAttributeValueModel;
            if (!valueTemp) return;
            item.variationAttributeValueId = valueTemp.id;
            onChange({ ...model });
          }}
          isMultiple={false}
          value={item.variationAttributeValueId}
          disabled={isUnitProduct || isUpdateMode}
        />
      ),
    },
    {
      title: "",
      width: "100px",
      render: (attribute, item, index) =>
        !model.mainVariationProductId && !isUpdateMode && !isUnitProduct ? (
          <Popconfirm
            title={t(CoreResource, "QuestionRemove")}
            onConfirm={() => {
              model.variationAttributes.splice(index, 1);
              onChange({
                ...model,
                variationAttributes: [...model.variationAttributes],
              });
            }}
            okText="Có"
            cancelText="Không"
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <div>
      <Collapse defaultActiveKey={["1"]} className="mb-4">
        <Collapse.Panel key="1" header="Thông tin sản phẩm">
          <Row gutter={[16, 16]}>
            <Col span="12">
              <Input.Group className="mb-4">
                <Typography.Text>Mã hàng: </Typography.Text>
                <Input
                  value={model.skuCode}
                  onChange={(e) => {
                    onChange({ ...model, skuCode: e.target.value });
                  }}
                  placeholder="Hệ thống tự tạo"
                ></Input>
              </Input.Group>
              <Input.Group className="mb-4">
                <Typography.Text>Mã vạch: </Typography.Text>
                <Input
                  value={model.barCode}
                  onChange={(e) => {
                    onChange({ ...model, barCode: e.target.value });
                  }}
                ></Input>
              </Input.Group>
              <Input.Group className="mb-4">
                <Typography.Text>Tên sản phẩm: </Typography.Text>
                <Input
                  disabled={!!model.mainVariationProductId || isUnitProduct}
                  value={model.name}
                  onChange={(e) => {
                    onChange({
                      ...model,
                      name: e.target.value,
                      seoFields: isUpdateMode
                        ? model.seoFields
                        : ({
                            ...model.seoFields,
                            metaTitle: e.target.value,
                          } as SeoModel),
                      slug: cleanUnicode(e.target.value),
                    });
                  }}
                ></Input>
              </Input.Group>
              <Input.Group className="mb-4">
                <Typography.Text>Danh mục chính: </Typography.Text>
                <Select
                  disabled={
                    !!model.mainVariationProductId ||
                    isUnitProduct ||
                    model.isModifier
                  }
                  className="w-100"
                  placeholder="Chọn danh mục chính"
                  value={model.mainCategoryId}
                  onChange={(e) => {
                    onChange({ ...model, mainCategoryId: e });
                  }}
                  showSearch
                  allowClear
                  filterOption={(input, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {productCategories.map((cate) => (
                    <Select.Option value={cate.id}>{cate.name}</Select.Option>
                  ))}
                </Select>
              </Input.Group>
              <Input.Group className="mb-4">
                <Typography.Text>Danh mục phụ: </Typography.Text>
                <Select
                  disabled={
                    !!model.mainVariationProductId ||
                    isUnitProduct ||
                    model.isModifier
                  }
                  className="w-100"
                  placeholder="Chọn danh mục phụ"
                  value={model.relatedCategoryIds ?? []}
                  onChange={(e) => {
                    onChange({ ...model, relatedCategoryIds: e ?? [] });
                  }}
                  showSearch
                  filterOption={(input, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  mode="multiple"
                  allowClear
                >
                  {productCategories.map((cate) => (
                    <Select.Option value={cate.id}>{cate.name}</Select.Option>
                  ))}
                </Select>
              </Input.Group>
              <Input.Group className="mb-4">
                <Typography.Text>Tags: </Typography.Text>
                <TagSelector
                  onChange={(values) => {
                    values = values as TagModel[];
                    onChange({
                      ...model,
                      tags: values,
                    });
                  }}
                  isMultiple={true}
                  value={model.tags ? model.tags.map((m) => m.id) : []}
                />
              </Input.Group>
            </Col>
            <Col span="12">
              <Input.Group className="mb-4">
                <Typography.Text>Giá bán: </Typography.Text>
                <Input
                  value={model.price}
                  onChange={(e) => {
                    onChange({ ...model, price: e.target.valueAsNumber });
                  }}
                  type="number"
                ></Input>
              </Input.Group>
              <Input.Group className="mb-4">
                <Typography.Text>Giá khuyến mãi: </Typography.Text>
                <Input
                  type="number"
                  value={model.promotionPrice}
                  onChange={(e) => {
                    onChange({
                      ...model,
                      promotionPrice: e.target.valueAsNumber,
                    });
                  }}
                ></Input>
              </Input.Group>
              <Row gutter={[16, 16]} className="mb-4">
                <Col span="12">
                  <Typography.Text>Từ ngày: </Typography.Text>
                  <DatePicker
                    format={momentDateTimeFormat()}
                    value={
                      model.promotionFromDate
                        ? momentTz.tz(model.promotionFromDate, currentTimeZone)
                        : undefined
                    }
                    onChange={(e, dateString) => {
                      onChange({
                        ...model,
                        promotionFromDate: momentTz
                          .tz(
                            dateString,
                            momentDateTimeFormat(),
                            currentTimeZone
                          )
                          .toDate(),
                      });
                    }}
                    showTime
                    showSecond={false}
                    className="w-100"
                  />
                </Col>
                <Col span="12">
                  <Typography.Text>Đến ngày: </Typography.Text>
                  <DatePicker
                    format={momentDateTimeFormat()}
                    value={
                      model.promotionToDate
                        ? momentTz.tz(model.promotionToDate, currentTimeZone)
                        : undefined
                    }
                    onChange={(e, dateString) => {
                      onChange({
                        ...model,
                        promotionToDate: momentTz
                          .tz(
                            dateString,
                            momentDateTimeFormat(),
                            currentTimeZone
                          )
                          .toDate(),
                      });
                    }}
                    showTime
                    showSecond={false}
                    className="w-100"
                  />
                </Col>
              </Row>
              <Input.Group className="mb-4">
                <Typography.Text>Đơn vị tính: </Typography.Text>
                <UnitSelector
                  onChange={(value) => {
                    let valueTemp = value as UnitModel;
                    if (!valueTemp) {
                      model.unit = undefined;
                    } else {
                      model.unit = {
                        id: valueTemp.id,
                        unitName: valueTemp?.unitName,
                      } as UnitModel;
                    }
                    onChange({ ...model });
                  }}
                  isMultiple={false}
                  value={model.unit?.id}
                />
              </Input.Group>
              <Input.Group className="mb-4">
                <Typography.Text>Giá trị quy đổi: </Typography.Text>
                <Input
                  disabled={!isUnitProduct}
                  type="number"
                  value={model.unitRate}
                  onChange={(e) => {
                    onChange({
                      ...model,
                      unitRate: e.target.valueAsNumber,
                    });
                  }}
                ></Input>
              </Input.Group>
            </Col>
            <Col span="24">
              <Input.Group className="mb-4">
                <Space>
                  <Checkbox
                    checked={model.isActive}
                    onChange={(e) => {
                      onChange({ ...model, isActive: e.target.checked });
                    }}
                  >
                    Còn kinh doanh
                  </Checkbox>
                  {/* {(!model.isVariation || !model.mainVariationProductId) && (
                    <Checkbox
                      checked={model.isInventory}
                      onChange={(e) => {
                        onChange({ ...model, isInventory: e.target.checked });
                      }}
                    >
                      Is Inventory
                    </Checkbox>
                  )}
                  <Checkbox
                    checked={model.isModifier}
                    onChange={(e) => {
                      onChange({ ...model, isModifier: e.target.checked });
                    }}
                  >
                    Is Modifier
                  </Checkbox> */}
                </Space>
              </Input.Group>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>
      <Collapse className="mb-4">
        <Collapse.Panel key="wholesale" header="Giá sỉ">
          <Table
            className="mb-4"
            columns={wholesaleColumns}
            pagination={false}
            dataSource={model.wholesale?.items ?? []}
            size="small"
          />
          <div>
            <Button onClick={_handleAddWholeSale} icon={<PlusOutlined />}>
              Thêm giá sỉ
            </Button>
          </div>
        </Collapse.Panel>
      </Collapse>
      <Collapse className="mb-4">
        <Collapse.Panel
          key="price-by-subtotal"
          header="Giá theo giá trị đơn hàng"
        >
          <Table
            className="mb-4"
            columns={priceBySubTotalColumns}
            pagination={false}
            dataSource={model.priceBySubTotal?.items ?? []}
            size="small"
          />
          <div>
            <Button onClick={_handleAddPriceBySubTotal} icon={<PlusOutlined />}>
              Thêm giá
            </Button>
          </div>
        </Collapse.Panel>
      </Collapse>
      <Collapse className="mb-4">
        <Collapse.Panel header="Hình ảnh" key="1">
          <UploadFileLocal
            album={model.images ?? []}
            multiple={true}
            isUploadModal={false}
            onChange={(newFileList: string[]) => {
              onChange({ ...model, images: newFileList });
            }}
          />
        </Collapse.Panel>
      </Collapse>

      {((!isUpdateMode && !isUnitProduct) || model.isVariation) &&
        !model.isModifier && (
          <Collapse className="mb-4">
            <Collapse.Panel header="Thuộc tính biến thể sản phẩm" key="1">
              <Table
                bordered
                className="mb-4"
                columns={variationColumns}
                pagination={false}
                dataSource={model.variationAttributes ?? []}
                size="small"
              />
              {!model.mainVariationProductId &&
                !isUpdateMode &&
                !isUnitProduct && (
                  <div className="mb-4">
                    <Button
                      onClick={_handleAddVariationAttribute}
                      icon={<PlusOutlined />}
                    >
                      Thêm thuộc tính
                    </Button>
                  </div>
                )}
            </Collapse.Panel>
          </Collapse>
        )}

      <Collapse className="mb-4">
        <Collapse.Panel header="Kích thước" key="kich-thuoc">
          <Row gutter={[16, 16]}>
            <Col span="6">
              <Typography.Text>Cân nặng (g): </Typography.Text>
              <Input
                type="number"
                value={model.size?.weight}
                onChange={(e) => {
                  onChange({
                    ...model,
                    size: { ...model.size, weight: e.target.valueAsNumber },
                  });
                }}
              ></Input>
            </Col>
            <Col span="6">
              <Typography.Text>Chiều dài (cm): </Typography.Text>
              <Input
                type="number"
                value={model.size?.length}
                onChange={(e) => {
                  onChange({
                    ...model,
                    size: { ...model.size, length: e.target.valueAsNumber },
                  });
                }}
              ></Input>
            </Col>
            <Col span="6">
              <Typography.Text>Chiều rộng (cm): </Typography.Text>
              <Input
                type="number"
                value={model.size?.width}
                onChange={(e) => {
                  onChange({
                    ...model,
                    size: { ...model.size, width: e.target.valueAsNumber },
                  });
                }}
              ></Input>
            </Col>
            <Col span="6">
              <Typography.Text>Chiều cao (cm): </Typography.Text>
              <Input
                type="number"
                value={model.size?.height}
                onChange={(e) => {
                  onChange({
                    ...model,
                    size: { ...model.size, height: e.target.valueAsNumber },
                  });
                }}
              ></Input>
            </Col>
          </Row>{" "}
        </Collapse.Panel>
      </Collapse>
    </div>
  );
}
